import { useCallback } from 'react'
import { intl } from '../../../../i18n'
import {
  TaskActualWorkApprovalStatusSearchCondition,
  useOnSingleValueChangedGenerator,
} from '../hooks/useSearchCondition'
import { Button } from '../../../components/buttons'
import SearchConditionSelectBoxCheckmarks from '../../../components/search-forms/search-panel/SearchConditionSelectBoxCheckmarks'
import { SelectOption } from '../../../model/selectOption'
import { Box, Dialog, DialogContent, Typography } from '@mui/material'
import { Checkbox } from '../../../components/inputs/Checkbox'

const dialogStyles = {
  zIndex: 1200,
  '& .MuiDialog-container': {
    alignItems: 'flex-start',
    paddingTop: '120px',
  },
  '& .MuiPaper-root': {
    maxWidth: '600px',
    width: '90%',
    margin: '0 auto',
    overflow: 'visible',
  },
  '& .MuiPopper-root': {
    zIndex: 1300,
    minWidth: '400px !important',
    maxWidth: '400px !important',
    width: '400px !important',
  },
}

const dialogContentStyles = {
  overflow: 'visible',
}

const contentBoxStyles = {
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  gap: '20px',
  minHeight: '200px',
}

const titleStyles = {
  marginBottom: '10px',
  fontWeight: 600,
}

const selectBoxWrapperStyles = {
  width: '100%',
  maxWidth: '500px',
}

const customBoxStyles = {
  border: '1px solid #D8DDE5',
  borderRadius: '4px',
  padding: '5px 10px',
  backgroundColor: '#ffffff',
  height: '50px',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
}

const titleTextStyles = {
  color: '#7B8CAA',
  minWidth: '80px',
  marginRight: '10px',
}

const labelTextStyles = {
  color: '#7B8CAA',
  marginRight: '10px',
}

const checkboxStyles = {
  marginRight: '8px',
}

const buttonContainerStyles = {
  display: 'flex',
  justifyContent: 'center',
  gap: '10px',
  paddingTop: '10px',
  marginTop: '20px',
  position: 'relative',
}

const countTextStyles = {
  width: '100%',
  textAlign: 'center',
  marginBottom: 1,
  position: 'absolute',
  top: '-25px',
  color: '#7B8CAA',
}

type Props = {
  open: boolean
  onClose: () => void
  searchCondition: TaskActualWorkApprovalStatusSearchCondition
  onChangeCondition: (
    updated: Partial<TaskActualWorkApprovalStatusSearchCondition>
  ) => void
  resetCondition: () => void
  search: () => void
  userCount?: number
}

const TaskActualWorkApprovalStatusSearchConditionDialog = ({
  open,
  onClose,
  searchCondition,
  onChangeCondition,
  resetCondition,
  search,
  userCount,
}: Props) => {
  const { onChangeValueGenerator } = useOnSingleValueChangedGenerator(
    searchCondition,
    onChangeCondition
  )

  const onSearch = useCallback(() => {
    search()
    onClose()
  }, [search, onClose])

  const approvalStatusOptions: SelectOption[] = [
    {
      value: 'UNREQUESTED',
      name: intl.formatMessage({
        id: 'taskActualWorkApprovalStatus.unRequested',
      }),
    },
    {
      value: 'UNAPPROVED',
      name: intl.formatMessage({
        id: 'taskActualWorkApprovalStatus.summary.unapproved',
      }),
    },
    {
      value: 'APPROVED',
      name: intl.formatMessage({
        id: 'taskActualWorkApprovalStatus.allApproved',
      }),
    },
  ]

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth={true}
      sx={dialogStyles}
    >
      <DialogContent sx={dialogContentStyles}>
        <Box sx={contentBoxStyles}>
          <Typography variant="h6" sx={titleStyles}>
            {intl.formatMessage({ id: 'projectClosingStatus.detailSearch' })}
          </Typography>
          <Box sx={selectBoxWrapperStyles}>
            <Box sx={customBoxStyles}>
              <Typography sx={titleTextStyles}>
                {intl.formatMessage({
                  id: 'taskActualWorkApprovalStatus.userName',
                })}
              </Typography>
              <Typography sx={labelTextStyles}>
                {intl.formatMessage({
                  id: 'taskActualWorkApprovalStatus.expired',
                })}
              </Typography>
              <Checkbox
                sx={checkboxStyles}
                value="expired"
                checked={searchCondition.excludingExpired || false}
                onChange={e =>
                  onChangeValueGenerator('excludingExpired')(e.target.checked)
                }
              />
            </Box>
          </Box>
          <Box sx={selectBoxWrapperStyles}>
            <SearchConditionSelectBoxCheckmarks
              title={intl.formatMessage({
                id: 'taskActualWorkApprovalStatus.approvalStatus',
              })}
              value={searchCondition.approvalStatus || []}
              options={approvalStatusOptions}
              onChange={onChangeValueGenerator('approvalStatus')}
            />
          </Box>
          <Box sx={buttonContainerStyles}>
            <Typography variant="body2" sx={countTextStyles}>
              {intl.formatMessage({
                id: 'taskActualWorkApprovalStatus.searchResult',
              })}{' '}
              {userCount || '0'}{' '}
              {intl.formatMessage({
                id: 'taskActualWorkApprovalStatus.count',
              })}
            </Typography>

            <Button
              size="m"
              colorPattern="monotone"
              variant="outlined"
              onClick={resetCondition}
            >
              <Typography>
                {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.reset' })}
              </Typography>
            </Button>
            <span>
              <Button
                size="m"
                colorPattern="skyBlue"
                variant="filled"
                onClick={onSearch}
              >
                <Typography>
                  {intl.formatMessage({
                    id: 'wbsItemSearchFilterPanel.search',
                  })}
                </Typography>
              </Button>
            </span>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default TaskActualWorkApprovalStatusSearchConditionDialog
